import { useSeo } from "@/lib/seo";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { AdminAddonOverrides } from "@/components/admin/AdminAddonOverrides";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { invalidatePromoCache } from "@/lib/promo";
import { Users, Home, TrendingUp, Euro, FileText, MessageSquare, Trash2, Shield, Search, Crown, Sparkles, Download, Ban, Edit3, X, Save, Link2, RefreshCw, CheckCircle2, AlertCircle, AlertTriangle, Mail, Send, Tag, Plus, History, Percent, Eye, Gift, Gauge, Megaphone, Repeat, BarChart3, FileKey2, Coins, ShieldCheck, Building2, Lock, Scale, Magnet, Handshake, Headphones, Store, Activity, GalleryHorizontal } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { ExtraKpis, ExpiryFeedbackCenter, NotificationBell, GlobalSearch, ExportButtons } from "@/components/AdminExtras";
import { AdminAgencyReports } from "@/components/AdminAgencyReports";
import { AdminAiActions } from "@/components/AdminAiActions";
import AdminPrivacy from "@/components/AdminPrivacy";
import AdminLegalDocs from "@/components/AdminLegalDocs";
import AdminOwnerLeads from "@/pages/admin/AdminOwnerLeads";
import AdminSupportChat from "@/components/AdminSupportChat";
import AdminPartnerships from "@/components/AdminPartnerships";
import { fmtDate } from "@/pages/crm/shared";
import AdminShowroom from "@/components/AdminShowroom";
import AdminHealthCenter from "@/components/AdminHealthCenter";
import { AdminFattura24 } from "@/components/AdminFattura24";
import { AdminLoginForm } from "@/components/AdminLoginForm";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AdminPartnerLogos } from "@/components/AdminPartnerLogos";

const TABS = [
    { id: "overview", label: "Panoramica", icon: Gauge, color: "text-blue-600" },
    { id: "users", label: "Utenti", icon: Users, color: "text-blue-600" },
    { id: "properties", label: "Annunci", icon: Megaphone, color: "text-emerald-600" },
    { id: "transactions", label: "Transazioni", icon: Repeat, color: "text-orange-600" },
    { id: "invoices", label: "Fatture", icon: FileText, color: "text-purple-600" },
    { id: "reports", label: "Report agenzie", icon: BarChart3, color: "text-blue-600" },
    { id: "ai", label: "Azioni AI", icon: Sparkles, color: "text-teal-600" },
    { id: "fattura24", label: "Configurazione Fattura24", icon: FileKey2, color: "text-amber-600" },
    { id: "email", label: "Email broadcast", icon: Mail, color: "text-orange-600" },
    { id: "discounts", label: "Codici sconto", icon: Tag, color: "text-emerald-600" },
    { id: "plans", label: "Prezzi piani", icon: Coins, color: "text-amber-600" },
    { id: "audit", label: "Audit log", icon: ShieldCheck, color: "text-indigo-600" },
    { id: "offices", label: "Uffici agenzie", icon: Building2, color: "text-blue-600" },
    { id: "privacy", label: "Privacy", icon: Lock, color: "text-emerald-600" },
    { id: "legal", label: "Documenti Legali", icon: Scale, color: "text-purple-600" },
    { id: "owner_leads", label: "Gestione Lead", icon: Magnet, color: "text-blue-600" },
    { id: "partnerships", label: "Partnership", icon: Handshake, color: "text-emerald-600" },
    { id: "partner_logos", label: "Agenzie Partner", icon: GalleryHorizontal, color: "text-blue-600" },
    { id: "support", label: "Chat Supporto", icon: Headphones, color: "text-purple-600" },
    { id: "showroom", label: "VendiDirect Showroom", icon: Store, color: "text-blue-600" },
    { id: "health", label: "Stato e Diagnosi del Sistema", icon: Activity, color: "text-orange-600" },
];

export default function Admin() {
    useSeo({ title: "Super Admin | VendiDirect", description: "Pannello di controllo amministrativo.", noindex: true });
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("overview");
    const [data, setData] = useState(null);
    const [tabData, setTabData] = useState(null);
    const [search, setSearch] = useState("");
    const [userFilters, setUserFilters] = useState({});

    const loadOverview = useCallback(() => {
        if (!user?.is_admin) return;
        api.get("/admin/overview").then((response) => setData(response.data));
    }, [user]);

    useEffect(() => {
        loadOverview();
        if (!user?.is_admin) return undefined;
        const interval = window.setInterval(loadOverview, 60000);
        return () => window.clearInterval(interval);
    }, [loadOverview, user]);

    useEffect(() => {
        if (!user?.is_admin) return;
        const endpoint = tab === "users" ? "/admin/users" : tab === "properties" ? "/admin/properties" : tab === "transactions" ? "/admin/transactions" : tab === "invoices" ? "/admin/invoices" : null;
        if (!endpoint) return;
        const params = tab === "users" || tab === "properties" ? { q: search } : {};
        if (tab === "users") {
            const plans = [];
            if (userFilters.premium) plans.push("premium");
            if (userFilters.agenzia) plans.push("agenzia");
            if (plans.length) params.plan = plans.join(",");
            if (userFilters.has_feed) params.has_feed = 1;
        }
        api.get(endpoint, { params }).then((r) => setTabData(r.data)).catch(() => setTabData({ items: [], count: 0, error: true }));
    }, [tab, user, search, userFilters]);

    if (loading) return <div className="pt-32 text-center text-ink-400">Caricamento...</div>;
    if (!user) return <AdminLoginForm />;
    if (!user.is_admin) {
        return (
            <div className="pt-32 max-w-2xl mx-auto px-6 text-center">
                <Shield size={36} className="text-gold mx-auto mb-4" />
                <h1 className="font-serif text-3xl text-ink-50">Area riservata</h1>
                <p className="text-ink-400 mt-4">Questa sezione è accessibile solo all'amministratore di sistema.</p>
                <Link to="/" className="mt-8 inline-block px-6 py-3 cta-outline">Torna alla home</Link>
            </div>
        );
    }

    return (
        <div data-testid="admin-page" data-vd-manager="true" className="pt-28 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <div className="flex items-center gap-3 mb-2">
                <Shield size={18} className="text-gold" />
                <span className="label-eyebrow text-gold">Area Proprietario · {user.email}</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink-50">Gestione vendidirect.it</h1>
            <p className="text-sm text-ink-400 mt-4 max-w-3xl">
                Sei l'unico ad accedere a questa pagina. Qui vedi <span className="text-ink-100">tutti i dati del sito</span>: ricavi, abbonati, annunci, transazioni Stripe e fatture. Gli altri utenti possono accedere solo alla loro dashboard personale.
            </p>

            <div className="flex gap-3 mt-8 items-center">
                <GlobalSearch onOpenTab={(t, q) => { setTab(t); if (q !== undefined) setSearch(q); }} />
                <NotificationBell />
            </div>

            <div className="admin-neu-panel flex flex-wrap gap-3 mt-8 p-5" data-testid="admin-tabs-grid">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`neu-btn no-pill inline-flex items-center gap-2.5 px-4 py-3 text-sm font-semibold whitespace-nowrap ${active ? "neu-btn-active" : ""}`}
                        >
                            <span className="neu-icon-chip">
                                <Icon size={15} className={active ? "text-white" : t.color} />
                            </span>
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-10">
                {tab === "overview" && <Overview data={data} />}
                {tab === "users" && <UsersTab data={tabData} search={search} setSearch={setSearch} filters={userFilters} setFilters={setUserFilters} onUpdate={() => {
                    const params = { q: search };
                    const plans = [];
                    if (userFilters.premium) plans.push("premium");
                    if (userFilters.agenzia) plans.push("agenzia");
                    if (plans.length) params.plan = plans.join(",");
                    if (userFilters.has_feed) params.has_feed = 1;
                    return api.get("/admin/users", { params }).then((r) => setTabData(r.data)).catch(() => setTabData({ items: [], count: 0, error: true }));
                }} />}
                {tab === "properties" && <PropertiesTab data={tabData} search={search} setSearch={setSearch} onDelete={() => api.get("/admin/properties", { params: { q: search } }).then((r) => setTabData(r.data))} />}
                {tab === "transactions" && <TransactionsTab data={tabData} onRefresh={() => api.get("/admin/transactions").then((r) => setTabData(r.data))} />}
                {tab === "invoices" && <InvoicesTab data={tabData} onRefresh={() => api.get("/admin/invoices").then((r) => setTabData(r.data))} />}
                {tab === "reports" && <AdminAgencyReports />}
                {tab === "ai" && <AdminAiActions />}
                {tab === "fattura24" && <AdminFattura24 />}
                {tab === "email" && <EmailBroadcastTab />}
                {tab === "discounts" && <DiscountCodesTab />}
                {tab === "plans" && <PricingProfilesTab />}
                {tab === "audit" && <AuditLogTab />}
                {tab === "offices" && <AdminOfficesOverview />}
                {tab === "privacy" && <AdminPrivacy />}
                {tab === "legal" && <AdminLegalDocs />}
                {tab === "owner_leads" && <AdminOwnerLeads />}
                {tab === "partnerships" && <AdminPartnerships />}
                {tab === "partner_logos" && <AdminPartnerLogos />}
                {tab === "support" && <AdminSupportChat />}
                {tab === "showroom" && <AdminShowroom />}
                {tab === "health" && <AdminHealthCenter />}
            </div>
        </div>
    );
}

const KPI_ICON_COLORS = ["", "vd-kpi-icon-highlight", "vd-kpi-icon-amber", "vd-kpi-icon-violet"];
const Kpi = ({ icon, label, value, sub, testid, isPrice, colorIndex = 0 }) => (
    <div data-testid={testid} className="vd-kpi-card p-5 flex items-start gap-4">
        <div className={`vd-kpi-icon ${KPI_ICON_COLORS[colorIndex % KPI_ICON_COLORS.length]}`}>{icon}</div>
        <div className="min-w-0">
            <div className="text-xs text-ink-400 tracking-[0.15em] uppercase truncate">{label}</div>
            <div className={`font-serif text-2xl mt-1 ${isPrice ? "text-price" : "text-ink-50"}`}>{value}</div>
            {sub && <div className="text-xs text-ink-400 mt-1">{sub}</div>}
        </div>
    </div>
);

const Overview = ({ data }) => {
    if (!data) return <div className="text-ink-400">Caricamento KPI...</div>;
    const k = data.kpi;
    return (
        <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi icon={<Euro size={14} />} label="MRR" value={formatPrice(k.mrr)} sub="ricavo mensile ricorrente" testid="kpi-mrr" isPrice colorIndex={0} />
                <Kpi icon={<TrendingUp size={14} />} label="Ricavo 30gg" value={formatPrice(k.revenue_30d)} sub={`tot. storico: ${formatPrice(k.total_revenue)}`} testid="kpi-revenue" isPrice colorIndex={1} />
                <Kpi icon={<Users size={14} />} label="Utenti totali" value={k.total_users} sub={`+${k.new_users_30d} ultimi 30gg`} testid="kpi-users" colorIndex={2} />
                <Kpi icon={<Crown size={14} />} label="Abbonamenti attivi" value={k.active_subscriptions} sub={`${k.paid_subscriptions || 0} paganti · ${k.trial_users || 0} in prova`} testid="kpi-subs" colorIndex={3} />
                <Kpi icon={<Home size={14} />} label="Annunci pubblicati" value={k.total_properties} sub={`+${k.new_properties_30d} ultimi 30gg`} testid="kpi-props" colorIndex={0} />
                <Kpi icon={<FileText size={14} />} label="Fatture emesse" value={k.invoices_total} testid="kpi-invoices" colorIndex={1} />
                <Kpi icon={<Repeat size={14} />} label="Transazioni riuscite" value={k.transactions_total} sub="solo Stripe live" testid="kpi-transactions" colorIndex={2} />
                <Kpi icon={<Sparkles size={14} />} label="Conv. signup→attivo" value={`${k.signup_to_active_rate || k.trial_to_paid_rate}%`} testid="kpi-conv" colorIndex={2} />
                <Kpi icon={<MessageSquare size={14} />} label="Messaggi chat" value={data.engagement.chat_messages} testid="kpi-chat" colorIndex={3} />
            </div>

            <ExtraKpis />

            <ExpiryFeedbackCenter />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
                <Chart title="Nuovi iscritti (30gg)" data={data.timeseries.users} color="#D4AF6A" testid="chart-users" />
                <Chart title="Ricavo giornaliero €" data={data.timeseries.revenue} color="#E5C587" testid="chart-revenue" />
                <Chart title="Nuovi annunci (30gg)" data={data.timeseries.properties} color="#C29B57" testid="chart-props" />
                <PlansChart data={data.plans_distribution} testid="chart-plans" />
            </div>
        </>
    );
};

const Chart = ({ title, data, color, testid }) => (
    <div className="border border-white/10 p-6 bg-ink-900/40" data-testid={testid}>
        <div className="label-eyebrow mb-4">{title}</div>
        <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#A1A1AA" }} tickFormatter={(v) => v.slice(-5)} />
                    <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid #27272a", fontSize: 12 }} labelStyle={{ color: "#D4AF6A" }} />
                    <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const PlansChart = ({ data, testid }) => {
    const arr = Object.entries(data || {}).map(([k, v]) => ({ plan: k || "—", count: v }));
    return (
        <div data-testid={testid} className="border border-white/10 p-6 bg-ink-900/40">
            <div className="label-eyebrow mb-4">Distribuzione piani</div>
            <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                    <BarChart data={arr}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="plan" tick={{ fontSize: 11, fill: "#A1A1AA" }} />
                        <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid #27272a", fontSize: 12 }} labelStyle={{ color: "#D4AF6A" }} />
                        <Bar dataKey="count" fill="#D4AF6A" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const SearchBar = ({ search, setSearch }) => (
    <div className="relative max-w-md flex-1">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome o email..." className="w-full pl-10 pr-4 py-2.5 bg-transparent border border-white/15 text-ink-50 focus:border-gold outline-none text-sm" />
    </div>
);

// Mapping piani commerciali (allineato a backend PLAN_LABELS 04/02/2026)
const PLAN_LABELS = {
    base: "Privato Base",
    premium: "Agenzia Pro (€69 · Sincro Feed)",
    agenzia: "Agenzia Full (€129 · VD Manager)",
};
const PLAN_SHORT = {
    base: "Privato Base",
    premium: "Agenzia Pro",
    agenzia: "Agenzia Full",
};
const FEEDBACK_EMOJI = { great: "😍", good: "🙂", meh: "😐", bad: "😞" };

const FeedStatusCell = ({ feed }) => {
    if (!feed || !feed.configured) return <span className="text-ink-400/40 text-xs" title="Nessun feed">—</span>;
    const isError = feed.state === "error";
    const label = isError ? "Errore" : "OK";
    const tip = [
        feed.url ? `Feed: ${feed.url}` : null,
        feed.source_type ? `Tipo: ${feed.source_type.toUpperCase()}` : null,
        feed.last_sync_at ? `Ultima sincro: ${new Date(feed.last_sync_at).toLocaleString("it-IT")}` : "Nessuna sincro effettuata",
        isError ? `⚠ Errore: ${feed.last_error}` : null,
        typeof feed.hits === "number" ? `Hits totali: ${feed.hits}` : null,
    ].filter(Boolean).join("\n");
    const cls = isError ? "text-red-400 hover:text-red-300 border-red-500/40" : "text-green-400 hover:text-green-300 border-green-500/40";
    const Icon = isError ? AlertTriangle : CheckCircle2;
    return (
        <a
            href={feed.url || "#"}
            target={feed.url ? "_blank" : undefined}
            rel="noreferrer"
            onClick={(e) => { if (!feed.url) e.preventDefault(); }}
            title={tip}
            className={`inline-flex items-center gap-1 text-[11px] whitespace-nowrap border px-1.5 py-0.5 ${cls} transition-colors`}
            data-testid={`feed-status-${feed.state}`}
        >
            <Icon size={11} /> {label}
        </a>
    );
};

const UsersTab = ({ data, search, setSearch, onUpdate, filters, setFilters }) => {
    const [editUser, setEditUser] = useState(null);
    const updatePlan = async (uid, plan_id) => {
        try {
            await api.put(`/admin/users/${uid}`, { plan_id });
            onUpdate();
        } catch (err) {
            alert(err?.response?.data?.detail || "Errore aggiornamento piano");
        }
    };
    const impersonate = async (u) => {
        if (!window.confirm(`Entrare come ${u.name} (${u.email})?\nVedrai l'app esattamente come la vede l'utente. Potrai uscire in qualsiasi momento dal banner giallo in alto.`)) return;
        try {
            await api.post(`/admin/impersonate/${u.user_id}`);
            // Force full reload so all components pick up the new identity
            window.location.href = "/dashboard";
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore impersonate");
        }
    };
    const toggleFilter = (key) => setFilters({ ...filters, [key]: !filters[key] });
    return (
        <>
            <div className="flex flex-col gap-3 mb-6">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                    <SearchBar search={search} setSearch={setSearch} />
                    <ExportButtons entity="users" />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="users-filter-bar">
                    <span className="text-ink-400 uppercase tracking-widest mr-1">Filtri:</span>
                    <FilterChip active={filters.premium} onClick={() => toggleFilter("premium")} testid="filter-premium">Agenzia Pro (€69)</FilterChip>
                    <FilterChip active={filters.agenzia} onClick={() => toggleFilter("agenzia")} testid="filter-agenzia">Agenzia Full (€129)</FilterChip>
                    <FilterChip active={filters.has_feed} onClick={() => toggleFilter("has_feed")} testid="filter-has-feed">Con Feed Attivo</FilterChip>
                    {(filters.premium || filters.agenzia || filters.has_feed) && (
                        <button data-testid="filter-clear" onClick={() => setFilters({})} className="text-ink-400 hover:text-gold ml-2 underline underline-offset-2">Reset</button>
                    )}
                </div>
            </div>
            {!data ? <div className="text-ink-400">Caricamento...</div> :
             data.error ? (
                <div data-testid="users-load-error" className="border border-red-500/40 bg-red-900/20 p-6 text-sm text-red-200 flex items-center justify-between gap-3">
                    <span>⚠ Errore nel caricamento utenti dal server. Riprova.</span>
                    <button onClick={onUpdate} className="px-4 py-2 border border-red-400/60 text-red-100 hover:bg-red-500/20">Riprova</button>
                </div>
             ) :
            <div className="space-y-2.5">
                <div className="p-3 grid grid-cols-12 gap-3 items-center text-[10px] uppercase tracking-widest text-ink-400 bg-ink-950/40 hidden md:grid">
                    <div className="col-span-2">Utente</div>
                    <div className="col-span-1">Registrato</div>
                    <div className="col-span-2">Abbonamento</div>
                    <div className="col-span-1">Ann.</div>
                    <div className="col-span-1">Giorni Rim.</div>
                    <div className="col-span-1">Feed</div>
                    <div className="col-span-1">Moduli</div>
                    <div className="col-span-1">Feedback</div>
                    <div className="col-span-1">Cambio piano</div>
                    <div className="col-span-1 text-right">Azioni</div>
                </div>
                {(data.items || []).map((u) => (
                    <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="p-4 grid grid-cols-12 gap-3 items-center text-sm vd-card vd-lead-row">
                        <div className="col-span-2 min-w-0">
                            <div className="text-ink-50 flex items-center gap-2 truncate">
                                {u.name}
                                {u.suspended && <span className="text-[10px] bg-red-900/40 text-red-400 px-2 py-0.5">SOSPESO</span>}
                            </div>
                            <div className="text-xs text-ink-400 truncate">{u.email}</div>
                        </div>
                        <div className="col-span-1 text-xs text-ink-400">{new Date(u.created_at).toLocaleDateString("it-IT")}</div>
                        <div className="col-span-2 text-xs">
                            {u.plan_id ? (
                                <span data-testid={`plan-label-${u.user_id}`} className={`inline-flex items-center gap-1 px-2 py-1 ${u.plan_effectively_active ? "bg-gold/20 text-gold" : "border border-white/10 text-ink-400"}`} title={u.plan_effectively_active ? "Attivo" : "Non attivo"}>
                                    {PLAN_SHORT[u.plan_id] || u.plan_id}
                                </span>
                            ) : <span className="text-ink-400/60">—</span>}
                            {u.free_period && (
                                <div data-testid={`free-period-status-${u.user_id}`} className={`mt-1 text-[10px] leading-tight ${u.free_period.status === "active" ? "text-green-300" : u.free_period.status === "grace" ? "text-amber-300" : "text-red-300"}`}>
                                    {u.free_period.status === "active" ? "GRATIS" : u.free_period.status === "grace" ? "TOLLERANZA" : "GRATIS SCADUTO"}
                                    <span className="block text-ink-400 mt-0.5">{u.free_period.status === "grace" ? "fino al" : "scade il"} {new Date(u.free_period.status === "grace" ? u.free_period.grace_expires_at : u.free_period.expires_at).toLocaleDateString("it-IT")}</span>
                                </div>
                            )}
                        </div>
                        <div className="col-span-1 text-xs text-ink-400">{u.listings_count}</div>
                        <div data-testid={`days-left-${u.user_id}`} className={`col-span-1 text-xs ${u.days_left != null && u.days_left <= 7 ? "text-amber-300" : "text-ink-400"}`}>
                            {u.days_left != null ? `${u.days_left}g` : "—"}
                        </div>
                        <div className="col-span-1"><FeedStatusCell feed={u.feed_status} /></div>
                        <div className="col-span-1"><AddonModulesCell userId={u.user_id} modules={u.addon_modules} /></div>
                        <div data-testid={`last-feedback-${u.user_id}`} className="col-span-1 text-xs text-ink-400" title={u.last_feedback ? new Date(u.last_feedback.created_at).toLocaleString("it-IT") : ""}>
                            {u.last_feedback ? `${FEEDBACK_EMOJI[u.last_feedback.value] || ""}` : "—"}
                        </div>
                        <div className="col-span-1">
                            <select data-testid={`plan-select-${u.user_id}`} value={u.plan_id || ""} onChange={(e) => updatePlan(u.user_id, e.target.value)} className="bg-transparent border border-white/15 px-1 py-1.5 text-[10px] text-ink-50 focus:border-gold outline-none w-full">
                                <option value="" className="bg-ink-950">— nessuno</option>
                                <option value="base" className="bg-ink-950">Privato Base</option>
                                <option value="premium" className="bg-ink-950">Agenzia Pro</option>
                                <option value="agenzia" className="bg-ink-950">Agenzia Full</option>
                            </select>
                        </div>
                        <div className="col-span-1 flex justify-end gap-1">
                            <button data-testid={`impersonate-${u.user_id}`} onClick={() => impersonate(u)} className="p-2 text-ink-100 hover:text-amber-300" title={`Entra come ${u.name}`}><Eye size={14} /></button>
                            <button data-testid={`edit-${u.user_id}`} onClick={() => setEditUser(u)} className="p-2 text-ink-100 hover:text-gold" title="Modifica"><Edit3 size={14} /></button>
                        </div>
                    </div>
                ))}
                {(data.items || []).length === 0 && (
                    <div data-testid="users-empty" className="p-10 text-center text-sm text-ink-400">Nessun utente corrisponde ai filtri selezionati.</div>
                )}
            </div>}
            {editUser && <UserEditModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); onUpdate(); }} />}
        </>
    );
};

const ADDON_SHORT = { acquisizione: "ACQ", nuovi_privati: "NP", next_seller: "NS" };
const addonTone = (m) => {
    if (!m) return "border border-white/10 text-ink-400/60";
    if (["included", "free", "admin", "paid"].includes(m.reason)) return "bg-gold/20 text-gold";
    if (m.reason === "trial") return "bg-blue-500/20 text-blue-300";
    if (m.reason === "trial_expired") return "bg-amber-500/15 text-amber-300";
    return "border border-white/10 text-ink-400/60";
};
const addonTitle = (m) => {
    if (!m) return "";
    const d = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");
    switch (m.reason) {
        case "included": return `${m.name}: incluso (VD Manager)`;
        case "free": return `${m.name}: gratuito per questa agenzia`;
        case "admin": return `${m.name}: attivato dall'admin`;
        case "trial": return `${m.name}: in prova fino al ${d(m.trial_ends_at)} · poi ${m.price_monthly}€/mese`;
        case "paid": return `${m.name}: abbonato · scade ${d(m.expires_at)}`;
        case "trial_expired": return `${m.name}: prova terminata il ${d(m.trial_ends_at)} · non attivo`;
        default: return `${m.name}: non attivo (${m.price_monthly}€/mese)`;
    }
};
const AddonModulesCell = ({ userId, modules }) => {
    if (!modules) return <span className="text-xs text-ink-400/60">—</span>;
    return (
        <div data-testid={`addon-modules-${userId}`} className="flex flex-wrap gap-1">
            {Object.entries(modules).map(([id, m]) => (
                <span key={id} data-testid={`addon-module-${id}-${userId}`} title={addonTitle(m)} className={`px-1.5 py-0.5 text-[9px] font-semibold tracking-wider ${addonTone(m)}`}>
                    {ADDON_SHORT[id] || id}{m.reason === "trial" && m.trial_ends_at ? ` ${Math.max(0, Math.ceil((new Date(m.trial_ends_at) - Date.now()) / 86400000))}g` : ""}
                </span>
            ))}
        </div>
    );
};

const FilterChip = ({ active, onClick, children, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors ${active ? "bg-gold/20 border-gold text-gold" : "border-white/15 text-ink-100 hover:border-gold/40"}`}
    >
        {children}
    </button>
);

const UserEditModal = ({ user, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: user.name || "", email: user.email || "",
        plan_id: user.plan_id || "", plan_active: user.plan_active || false,
        role: user.role || "privato", agency_name: user.agency_name || "",
        suspended: user.suspended || false,
    });
    const [saving, setSaving] = useState(false);
    const [showFreeMonths, setShowFreeMonths] = useState(false);
    const addonsRef = useRef(null);
    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/users/${user.user_id}`, form);
            if (form.role === "agenzia") await addonsRef.current?.save();
            onSaved();
        }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };
    const suspend = async () => {
        if (!window.confirm(`${user.suspended ? "Riattivare" : "Sospendere"} questo utente?`)) return;
        try { await api.post(`/admin/users/${user.user_id}/suspend`); onSaved(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
    };
    const remove = async () => {
        if (!window.confirm(`ELIMINARE definitivamente ${user.email}? Tutti i suoi annunci e dati saranno rimossi.`)) return;
        try { await api.delete(`/admin/users/${user.user_id}`); onSaved(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
    };
    const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm";
    return (
        <div data-testid="user-edit-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
            <div className="bg-ink-950 border border-gold/40 max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <span className="label-eyebrow">Modifica utente</span>
                        <h3 className="font-serif text-2xl text-ink-50 mt-2">{user.email}</h3>
                        <div className="text-xs text-ink-400 mt-1">ID: {user.user_id}</div>
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-gold"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <Field label="Nome"><input data-testid="edit-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputCls} /></Field>
                    <Field label="Email"><input data-testid="edit-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={inputCls} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Ruolo">
                            <select data-testid="edit-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className={inputCls}>
                                <option value="privato" className="bg-ink-950">privato</option>
                                <option value="agenzia" className="bg-ink-950">agenzia</option>
                            </select>
                        </Field>
                        <Field label="Piano">
                            <select data-testid="edit-plan" value={form.plan_id || ""} onChange={(e) => setForm({...form, plan_id: e.target.value})} className={inputCls}>
                                <option value="" className="bg-ink-950">— nessuno</option>
                                <option value="base" className="bg-ink-950">Privato Base</option>
                                <option value="premium" className="bg-ink-950">Agenzia Pro (€69 · Sincro Feed)</option>
                                <option value="agenzia" className="bg-ink-950">Agenzia Full (€129 · VD Manager)</option>
                            </select>
                        </Field>
                    </div>
                    <Field label="Nome agenzia (se ruolo agenzia)"><input value={form.agency_name} onChange={(e) => setForm({...form, agency_name: e.target.value})} className={inputCls} /></Field>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input data-testid="edit-plan-active" type="checkbox" checked={form.plan_active} onChange={(e) => setForm({...form, plan_active: e.target.checked})} className="w-4 h-4 accent-gold" />
                        <span className="text-sm text-ink-100">Abbonamento attivo</span>
                    </label>
                </div>

                {form.role === "agenzia" && <AdminAddonOverrides ref={addonsRef} userId={user.user_id} />}

                {form.role === "agenzia" && (
                    <div className="mt-6 border-t border-white/10 pt-6 space-y-3">
                        <button
                            data-testid="open-free-months"
                            onClick={() => setShowFreeMonths(true)}
                            className="w-full px-5 py-3 text-sm border border-gold/40 text-gold hover:bg-gold/10 flex items-center justify-center gap-2 transition-colors"
                        >
                            <Gift size={14} /> Aggiungi mesi gratuiti
                        </button>
                    </div>
                )}

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button data-testid="modal-save" onClick={save} disabled={saving} className="cta-gold px-5 py-3 text-sm flex items-center justify-center gap-2">
                        <Save size={14} /> {saving ? "Salvataggio..." : "Salva"}
                    </button>
                    <button data-testid="modal-suspend" onClick={suspend} className={`px-5 py-3 text-sm border flex items-center justify-center gap-2 transition-colors ${user.suspended ? "border-green-500/40 text-green-400 hover:border-green-400" : "border-yellow-500/40 text-yellow-400 hover:border-yellow-400"}`}>
                        <Ban size={14} /> {user.suspended ? "Riattiva" : "Sospendi"}
                    </button>
                    <button data-testid="modal-delete" onClick={remove} className="px-5 py-3 text-sm border border-red-500/40 text-red-400 hover:border-red-400 flex items-center justify-center gap-2">
                        <Trash2 size={14} /> Elimina account
                    </button>
                </div>
            </div>
            {showFreeMonths && (
                <FreeMonthsPanel
                    user={user}
                    onClose={() => setShowFreeMonths(false)}
                    onDone={() => { setShowFreeMonths(false); onSaved(); }}
                />
            )}
        </div>
    );
};

const FreeMonthsPanel = ({ user, onClose, onDone }) => {
    const [months, setMonths] = useState(1);
    const [note, setNote] = useState("");
    const [notify, setNotify] = useState(false);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    useEffect(() => {
        api.get(`/admin/users/${user.user_id}/free-months-history`)
            .then((r) => setHistory(r.data.items || []))
            .catch(() => setHistory([]))
            .finally(() => setLoadingHistory(false));
    }, [user.user_id]);

    // Preview nuova scadenza + stato pagante
    const now = new Date();
    const currentExp = user.plan_expires_at ? new Date(user.plan_expires_at) : null;
    const isCurrentlyPaying = currentExp && currentExp > now && !user.plan_promo;
    const startsFromCurrent = currentExp && currentExp > now;
    const preview = (() => {
        const base = startsFromCurrent ? new Date(currentExp) : new Date(now);
        const d = new Date(base);
        const day = d.getDate();
        d.setMonth(d.getMonth() + months);
        // clamp fine mese
        if (d.getDate() < day) d.setDate(0);
        return d;
    })();

    const submit = async () => {
        const noteTrim = note.trim();
        if (!noteTrim) { alert("La nota interna è obbligatoria"); return; }
        if (months < 1 || months > 24) { alert("Numero mesi non valido (1-24)"); return; }
        const label = months === 1 ? "1 mese" : `${months} mesi`;
        const ok = window.confirm(
            `Confermi di aggiungere ${label} gratuiti a ${user.agency_name || user.name} (${user.email})?` +
            (notify ? "\n\n⚠ L'agenzia riceverà un'email di notifica." : "\n\nL'agenzia NON riceverà alcuna notifica.")
        );
        if (!ok) return;
        setSaving(true);
        try {
            await api.post(`/admin/users/${user.user_id}/add-free-months`, {
                months, note: noteTrim, notify,
            });
            onDone();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore durante l'operazione");
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm";

    return (
        <div
            data-testid="free-months-panel"
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
            onClick={onClose}
        >
            <div className="bg-ink-950 border border-gold/40 max-w-xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <div className="flex items-center gap-2">
                            <Gift size={16} className="text-gold" />
                            <span className="label-eyebrow text-gold">Mesi gratuiti</span>
                        </div>
                        <h3 className="font-serif text-2xl text-ink-50 mt-2">{user.agency_name || user.name}</h3>
                        <div className="text-xs text-ink-400 mt-1">{user.email}</div>
                        {user.plan_expires_at && (
                            <div className="text-xs text-ink-400 mt-2">
                                Scadenza attuale: <span className="text-ink-100">{new Date(user.plan_expires_at).toLocaleDateString("it-IT")}</span>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-gold"><X size={18} /></button>
                </div>

                <div className="space-y-4">
                    <Field label="Numero mesi da aggiungere">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {[1, 2, 3, 6, 12].map((n) => (
                                <button
                                    key={n}
                                    data-testid={`fm-quick-${n}`}
                                    onClick={() => setMonths(n)}
                                    className={`px-3 py-1.5 text-xs border transition-colors ${
                                        months === n
                                            ? "border-gold bg-gold/15 text-gold"
                                            : "border-white/15 text-ink-100 hover:border-gold/60"
                                    }`}
                                >
                                    {n} {n === 1 ? "mese" : "mesi"}
                                </button>
                            ))}
                        </div>
                        <input
                            data-testid="fm-months"
                            type="number"
                            min={1}
                            max={24}
                            value={months}
                            onChange={(e) => setMonths(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
                            className={inputCls}
                        />
                        <div className="text-[11px] text-ink-400 mt-1">Puoi inserire da 1 a 24 mesi</div>
                    </Field>

                    <Field label="Nota interna (motivazione) *">
                        <textarea
                            data-testid="fm-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            placeholder="Es. Compensazione disservizio del 12/02, offerta commerciale, ecc."
                            className={inputCls}
                        />
                    </Field>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            data-testid="fm-notify"
                            type="checkbox"
                            checked={notify}
                            onChange={(e) => setNotify(e.target.checked)}
                            className="w-4 h-4 accent-gold mt-0.5"
                        />
                        <span className="text-sm text-ink-100">
                            Avvisa l'agenzia via email
                            <span className="block text-xs text-ink-400 mt-0.5">Se disattivato, l'agenzia non riceverà alcuna notifica.</span>
                        </span>
                    </label>

                    <div className="border border-gold/30 bg-gold/5 p-4">
                        <div className="text-xs text-ink-400 mb-1 tracking-wider uppercase">Nuova scadenza prevista</div>
                        <div className="font-serif text-xl text-gold">{preview.toLocaleDateString("it-IT")}</div>
                        {isCurrentlyPaying && (
                            <div className="mt-3 pt-3 border-t border-gold/20 text-[11px] text-ink-100 leading-relaxed">
                                <div className="text-gold font-medium mb-1">Abbonamento pagante attivo</div>
                                Il periodo pagato in corso <span className="text-ink-50">(fino al {currentExp.toLocaleDateString("it-IT")})</span> viene rispettato integralmente.
                                I <span className="text-ink-50">{months} {months === 1 ? "mese gratuito" : "mesi gratuiti"}</span> si aggiungono in coda: alla fine dell'estensione l'agenzia dovrà pagare nuovamente per rinnovare.
                            </div>
                        )}
                        {!isCurrentlyPaying && startsFromCurrent && (
                            <div className="mt-3 pt-3 border-t border-gold/20 text-[11px] text-ink-400 leading-relaxed">
                                Estensione in coda alla scadenza attuale del {currentExp.toLocaleDateString("it-IT")}.
                            </div>
                        )}
                        {!startsFromCurrent && (
                            <div className="mt-3 pt-3 border-t border-gold/20 text-[11px] text-ink-400 leading-relaxed">
                                {currentExp ? "Piano scaduto: il bonus parte da oggi." : "Nessun piano attivo: il bonus parte da oggi."}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex gap-3">
                    <button
                        data-testid="fm-cancel"
                        onClick={onClose}
                        className="flex-1 px-5 py-3 text-sm border border-white/15 text-ink-100 hover:border-white/30 transition-colors"
                    >
                        Annulla
                    </button>
                    <button
                        data-testid="fm-confirm"
                        onClick={submit}
                        disabled={saving || !note.trim()}
                        className="flex-1 cta-gold px-5 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Gift size={14} /> {saving ? "Salvataggio..." : "Conferma"}
                    </button>
                </div>

                <div className="mt-8 border-t border-white/10 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                        <History size={14} className="text-ink-400" />
                        <span className="label-eyebrow">Storico operazioni</span>
                    </div>
                    {loadingHistory ? (
                        <div className="text-xs text-ink-400">Caricamento...</div>
                    ) : history.length === 0 ? (
                        <div className="text-xs text-ink-400">Nessuna operazione precedente.</div>
                    ) : (
                        <div className="space-y-3 max-h-56 overflow-y-auto pr-2">
                            {history.map((h) => (
                                <div key={h.id} data-testid={`fm-history-${h.id}`} className="border border-white/10 p-3 text-xs">
                                    <div className="flex justify-between items-start gap-3">
                                        <div className="text-gold font-serif text-sm">
                                            +{h.months} {h.months === 1 ? "mese" : "mesi"}
                                        </div>
                                        <div className="text-ink-400">{new Date(h.created_at).toLocaleDateString("it-IT")}</div>
                                    </div>
                                    <div className="text-ink-400 mt-2">
                                        Admin: <span className="text-ink-100">{h.admin_email}</span>
                                    </div>
                                    <div className="text-ink-400 mt-1">
                                        Scadenza portata a: <span className="text-ink-100">{h.new_expires_at ? new Date(h.new_expires_at).toLocaleDateString("it-IT") : "—"}</span>
                                    </div>
                                    {h.note && (
                                        <div className="mt-2 pl-2 border-l border-gold/40 text-ink-100 italic">"{h.note}"</div>
                                    )}
                                    <div className="mt-2 text-[10px] uppercase tracking-wider">
                                        {h.notified ? (
                                            <span className="text-green-400">Agenzia avvisata</span>
                                        ) : (
                                            <span className="text-ink-400">Silenzioso · nessuna notifica</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">{label}</label>
        {children}
    </div>
);

const PropertiesTab = ({ data, search, setSearch, onDelete }) => {
    const remove = async (id) => {
        if (!window.confirm("Eliminare questo annuncio (azione admin)?")) return;
        await api.delete(`/admin/properties/${id}`);
        onDelete();
    };
    return (
        <>
            <div className="flex flex-col sm:flex-row gap-3 mb-2 items-start sm:items-center justify-between">
                <SearchBar search={search} setSearch={setSearch} />
                <ExportButtons entity="properties" />
            </div>
            {!data ? <div className="text-ink-400">Caricamento...</div> :
            <div className="space-y-2.5">
                {(data.items || []).map((p) => (
                    <div key={p.id} className="p-4 grid grid-cols-12 gap-3 items-center text-sm vd-card vd-lead-row">
                        <img src={p.photos?.[0]} alt="" className="col-span-1 w-12 h-12 object-cover" />
                        <Link to={`/annunci/${p.id}`} className="col-span-5 text-ink-50 hover:text-gold truncate">
                            {p.title} <span className="text-ink-400 text-xs">· {p.city}</span>
                        </Link>
                        <div className="col-span-2 text-price font-serif">{formatPrice(p.price)}</div>
                        <div className="col-span-2 text-xs text-ink-400">{p.views || 0} views</div>
                        <div className="col-span-2 flex justify-end">
                            <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 text-ink-100 hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>}
        </>
    );
};

const TransactionsTab = ({ data, onRefresh }) => {
    const [selected, setSelected] = useState(new Set());
    const items = data?.items || [];
    const toggle = (sid) => setSelected((s) => { const n = new Set(s); n.has(sid) ? n.delete(sid) : n.add(sid); return n; });
    const toggleAll = () => setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((t) => t.session_id))));
    const deleteSelected = async () => {
        if (!window.confirm(`Attenzione: questa azione è irreversibile. Vuoi davvero eliminare ${selected.size} record?`)) return;
        try {
            await api.post("/admin/transactions/bulk-delete", { session_ids: [...selected] });
            toast.success(`${selected.size} transazioni eliminate`);
            setSelected(new Set());
            onRefresh && onRefresh();
        } catch (e) {
            toast.error("Errore durante l'eliminazione");
        }
    };
    return !data ? <div className="text-ink-400">Caricamento...</div> : (
        <>
            <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-ink-400 cursor-pointer select-none">
                    <input data-testid="transactions-select-all" type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} className="vd-checkbox" />
                    Seleziona tutti ({items.length})
                </label>
                <div className="flex items-center gap-3">
                    {selected.size > 0 && (
                        <button data-testid="transactions-delete-selected" onClick={deleteSelected} className="vd-btn-danger px-5 py-2.5 text-sm flex items-center gap-2">
                            <Trash2 size={14} /> Elimina selezionati ({selected.size})
                        </button>
                    )}
                    <ExportButtons entity="transactions" />
                </div>
            </div>
            <div className="space-y-2.5">
                {items.map((t, idx) => (
                    <div key={`${t.session_id}_${idx}`} data-testid={`transaction-row-${t.session_id}`} className={`p-4 flex items-center gap-3 text-sm vd-card vd-lead-row ${selected.has(t.session_id) ? "vd-row-selected" : ""}`}>
                        <input data-testid={`transaction-checkbox-${t.session_id}`} type="checkbox" checked={selected.has(t.session_id)} onChange={() => toggle(t.session_id)} className="vd-checkbox shrink-0" />
                        <div className="grid grid-cols-12 gap-3 items-center flex-1">
                            <div className="col-span-4 text-ink-100 truncate">{t.user_email}</div>
                            <div className="col-span-2 text-xs text-ink-400">{new Date(t.created_at).toLocaleDateString("it-IT")}</div>
                            <div className="col-span-2 text-ink-100">{t.plan_id}</div>
                            <div className="col-span-2 text-price font-serif">{formatPrice(t.amount)}</div>
                            <div className="col-span-2 text-right text-xs"><span className={`px-2 py-1 ${t.payment_status === "paid" ? "bg-green-900/30 text-green-400" : "bg-ink-900 text-ink-400"}`}>{t.payment_status}</span></div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="p-6 text-center text-ink-400 text-sm">Nessuna transazione.</div>}
            </div>
        </>
    );
};

const InvoicesTab = ({ data, onRefresh }) => {
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selected, setSelected] = useState(new Set());
    const items = data?.items || [];
    const exportCSV = () => {
        const params = new URLSearchParams();
        if (fromDate) params.set("from_date", fromDate + "T00:00:00");
        if (toDate) params.set("to_date", toDate + "T23:59:59");
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/invoices.csv?${params.toString()}`, "_blank");
    };
    const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((i) => i.id))));
    const deleteSelected = async () => {
        if (!window.confirm(`Attenzione: questa azione è irreversibile. Vuoi davvero eliminare ${selected.size} record?`)) return;
        try {
            await api.post("/admin/invoices/bulk-delete", { ids: [...selected] });
            toast.success(`${selected.size} fatture eliminate`);
            setSelected(new Set());
            onRefresh && onRefresh();
        } catch (e) {
            toast.error("Errore durante l'eliminazione");
        }
    };
    return !data ? <div className="text-ink-400">Caricamento...</div> : (
        <>
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-end justify-between">
                <div className="flex gap-2 items-end">
                    <div>
                        <label className="block text-xs text-ink-400 mb-1">Da</label>
                        <input data-testid="invoice-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-1">A</label>
                        <input data-testid="invoice-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                    {selected.size > 0 && (
                        <button data-testid="invoices-delete-selected" onClick={deleteSelected} className="vd-btn-danger px-5 py-2.5 text-sm flex items-center gap-2">
                            <Trash2 size={14} /> Elimina selezionati ({selected.size})
                        </button>
                    )}
                    <ExportButtons entity="invoices" />
                    <button data-testid="export-invoices-csv" onClick={exportCSV} className="cta-gold px-5 py-2.5 text-sm flex items-center gap-2">
                        <Download size={14} /> Esporta CSV per commercialista
                    </button>
                </div>
            </div>
            <p className="text-xs text-ink-400 mb-4">CSV con tutti i campi richiesti dal commercialista: imponibile, aliquota IVA, IVA, totale, nota IVA, dati cliente completi. Compatibile con Excel/LibreOffice (separatore `;`, BOM UTF-8).</p>
            <label className="flex items-center gap-2 text-xs text-ink-400 cursor-pointer select-none mb-3">
                <input data-testid="invoices-select-all" type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} className="vd-checkbox" />
                Seleziona tutte ({items.length})
            </label>
            <div className="space-y-2.5">
                {items.map((i, idx) => (
                    <div key={`${i.id}_${idx}`} data-testid={`invoice-row-${i.id}`} className={`p-4 flex items-center gap-3 text-sm vd-card vd-lead-row ${selected.has(i.id) ? "vd-row-selected" : ""}`}>
                        <input data-testid={`invoice-checkbox-${i.id}`} type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} className="vd-checkbox shrink-0" />
                        <div className="grid grid-cols-12 gap-3 items-center flex-1">
                            <div className="col-span-3 font-serif text-ink-50">{i.number}</div>
                            <div className="col-span-3 text-ink-400 text-xs">{i.customer?.email}</div>
                            <div className="col-span-2 text-ink-100">{i.plan_name}</div>
                            <div className="col-span-2 text-price font-serif">{formatPrice(i.gross)}</div>
                            <div className="col-span-2 text-xs text-ink-400">{new Date(i.issued_at).toLocaleDateString("it-IT")}</div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="p-6 text-center text-ink-400 text-sm">Nessuna fattura emessa.</div>}
            </div>
        </>
    );
};


const EmailBroadcastTab = () => {
    const [form, setForm] = useState({ subject: "", body_html: "", audience: "all", cta_text: "", cta_url: "" });
    const [history, setHistory] = useState([]);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const loadHistory = async () => {
        try {
            const r = await api.get("/admin/email/history");
            setHistory(r.data.items || []);
        } catch (e) { console.error(e); }
    };
    useEffect(() => { loadHistory(); }, []);

    const send = async (test_only) => {
        if (!form.subject.trim() || !form.body_html.trim()) { alert("Compila oggetto e corpo email"); return; }
        if (!test_only && !window.confirm(`Inviare a tutti i destinatari del segmento '${form.audience}'?`)) return;
        setSending(true); setResult(null);
        try {
            const { data } = await api.post("/admin/email/broadcast", { ...form, test_only });
            setResult(data);
            await loadHistory();
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || "Errore" });
        } finally { setSending(false); }
    };

    return (
        <div data-testid="admin-email-tab" className="space-y-8">
            <div className="border border-white/10 p-6 bg-ink-900/40">
                <div className="flex items-center gap-3 mb-4">
                    <Mail size={20} className="text-gold" />
                    <h2 className="font-serif text-2xl text-ink-50">Email personalizzata</h2>
                </div>
                <p className="text-xs text-ink-400 mb-6">Invia comunicazioni a tutti gli iscritti o a un segmento. Il template è già brandizzato vendidirect.it. Usa <code className="px-1 bg-black/30">{"{name}"}</code> per personalizzare il nome del destinatario.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">Oggetto</label>
                        <input data-testid="email-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" placeholder="es. Novità di Marzo su vendidirect.it" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">Destinatari</label>
                        <select data-testid="email-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm">
                            <option value="all" className="bg-ink-950">Tutti gli utenti</option>
                            <option value="privates_free" className="bg-ink-950">Privati Free (senza piano attivo)</option>
                            <option value="plan:base" className="bg-ink-950">Privati Base (9,99€)</option>
                            <option value="plan:premium" className="bg-ink-950">Agenzie Pro (€69)</option>
                            <option value="plan:agenzia" className="bg-ink-950">Agenzie Full (€129)</option>
                            <option value="partners" className="bg-ink-950">Partner Attivi</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">Corpo email (HTML)</label>
                        <textarea
                            data-testid="email-body"
                            value={form.body_html}
                            onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                            rows={10}
                            className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm font-mono"
                            placeholder="<p>Ciao {name},</p><p>Volevamo aggiornarti su...</p>"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">CTA testo (facoltativo)</label>
                        <input data-testid="email-cta-text" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" placeholder="Scopri di più" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">CTA URL (facoltativo)</label>
                        <input data-testid="email-cta-url" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" placeholder="https://vendi-direct.it/..." />
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-6">
                    <button data-testid="email-send-test" onClick={() => send(true)} disabled={sending} className="cta-outline px-5 py-2.5 text-sm flex items-center gap-2"><Mail size={14} /> Invia test (a te stesso)</button>
                    <button data-testid="email-send-all" onClick={() => send(false)} disabled={sending} className="cta-gold px-5 py-2.5 text-sm flex items-center gap-2"><Send size={14} /> {sending ? "Invio..." : "Invia a tutti i destinatari"}</button>
                </div>

                {result && (
                    <div className={`mt-5 p-4 text-sm ${result.error ? "bg-red-900/20 border border-red-700/40 text-red-200" : "bg-green-900/20 border border-green-700/40 text-green-200"}`}>
                        {result.error ? result.error : `Invio completato: ${result.sent}/${result.recipients} email inviate (${result.errors} errori). ${result.test_only ? "[TEST]" : ""}`}
                    </div>
                )}
            </div>

            <div className="border border-white/10 p-6 bg-ink-900/40">
                <h3 className="font-serif text-xl text-ink-50 mb-4">Cronologia invii</h3>
                {history.length === 0 ? (
                    <p className="text-sm text-ink-400">Nessun invio ancora effettuato.</p>
                ) : (
                    <div className="space-y-2.5">
                        {history.map((h) => (
                            <div key={h.id} className="py-3 grid grid-cols-12 gap-3 items-center text-sm vd-card vd-lead-row px-3">
                                <div className="col-span-5 text-ink-50 font-serif">{h.subject}</div>
                                <div className="col-span-3 text-xs text-ink-400">{h.audience}{h.test_only ? " · TEST" : ""}</div>
                                <div className="col-span-2 text-gold">{h.sent}/{h.recipients_count}</div>
                                <div className="col-span-2 text-xs text-ink-400">{new Date(h.created_at).toLocaleString("it-IT")}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <EmailOutbox />
        </div>
    );
};


const EmailOutbox = () => {
    const [data, setData] = useState({ items: [], counts: {} });
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async (status = filter) => {
        setLoading(true);
        try {
            const r = await api.get("/admin/email/outbox", { params: { status, limit: 100 } });
            setData(r.data);
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(filter); }, [filter]);

    const retry = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/admin/email/outbox/${id}/retry`);
            await load(filter);
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore nel reinvio");
        } finally { setBusyId(null); }
    };

    // We need to use the same backend URL the api client uses; build via window.location is unreliable.
    const apiBase = api.defaults.baseURL || "";
    const buildPreviewUrl = (id) => `${apiBase}/admin/email/outbox/${id}/preview`;

    const badge = (status) => {
        const map = {
            sent: "border-blue-700/40 text-blue-300 bg-blue-900/20",
            delivered: "border-green-700/40 text-green-300 bg-green-900/20",
            bounced: "border-amber-700/40 text-amber-300 bg-amber-900/20",
            failed: "border-red-700/40 text-red-300 bg-red-900/20",
            pending: "border-blue-700/40 text-blue-300 bg-blue-900/20",
        };
        return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${map[status] || map.pending}`}>{status}</span>;
    };

    return (
        <div data-testid="email-outbox" className="border border-white/10 p-6 bg-ink-900/40">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Mail size={18} className="text-gold" />
                    <h3 className="font-serif text-xl text-ink-50">Outbox transazionali</h3>
                </div>
                <div className="flex gap-2 text-xs">
                    {["all", "sent", "delivered", "bounced", "failed"].map((s) => (
                        <button
                            key={s}
                            data-testid={`outbox-filter-${s}`}
                            onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 border transition-colors ${filter === s ? "border-gold text-gold bg-gold/5" : "border-white/15 text-ink-400 hover:border-white/30"}`}
                        >
                            {s === "all" ? "Tutte" : s === "sent" ? "Inviate" : s === "delivered" ? "Consegnate" : s === "bounced" ? "Respinte" : "Fallite"}{data.counts[s] != null ? ` (${data.counts[s]})` : ""}
                        </button>
                    ))}
                    <button data-testid="outbox-refresh" onClick={() => load(filter)} className="px-3 py-1.5 border border-white/15 text-ink-400 hover:text-gold hover:border-gold/40 transition-colors">
                        <RefreshCw size={12} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-ink-400">Caricamento...</div>
            ) : (data.items || []).length === 0 ? (
                <p data-testid="outbox-empty" className="text-sm text-ink-400">Nessuna email registrata in questo filtro.</p>
            ) : (
                <div className="space-y-2.5">
                    {(data.items || []).map((m) => (
                        <div key={m.id} data-testid={`outbox-${m.id}`} className="py-3 grid grid-cols-12 gap-3 items-center text-sm vd-card vd-lead-row px-3">
                            <div className="col-span-4 min-w-0">
                                <div className="text-ink-50 truncate" title={m.subject}>{m.subject}</div>
                                <div className="text-[11px] text-ink-400 truncate">→ {m.to}{m.delivered_to && m.delivered_to !== m.to ? ` (consegnata a ${m.delivered_to})` : ""}</div>
                            </div>
                            <div className="col-span-2">{badge(m.status)}</div>
                            <div className="col-span-3 text-[11px] text-ink-400 truncate" title={m.error || ""}>{m.error || "—"}</div>
                            <div className="col-span-2 text-[11px] text-ink-400">{new Date(m.created_at).toLocaleString("it-IT")}</div>
                            <div className="col-span-1 flex justify-end gap-1">
                                <a
                                    href={buildPreviewUrl(m.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`outbox-preview-${m.id}`}
                                    className="px-2 py-1 border border-white/15 text-[10px] text-ink-400 hover:text-gold hover:border-gold/40 transition-colors"
                                    title="Anteprima HTML"
                                >
                                    <Eye size={12} />
                                </a>
                                {(m.status === "failed" || m.status === "forwarded") && (
                                    <button
                                        data-testid={`outbox-retry-${m.id}`}
                                        onClick={() => retry(m.id)}
                                        disabled={busyId === m.id}
                                        className="px-2 py-1 border border-gold/40 text-[10px] text-gold hover:bg-gold hover:text-ink-950 transition-colors disabled:opacity-50"
                                        title="Reinvia"
                                    >
                                        <RefreshCw size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const DiscountCodesTab = () => {
    const [items, setItems] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ code: "", discount_percent: 10, plans: [], expires_at: "", max_uses: "", description: "" });

    const load = async () => {
        try { const r = await api.get("/admin/discount-codes"); setItems(r.data.items || []); } catch (e) { console.error(e); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.code.trim()) { alert("Inserisci un codice"); return; }
        setCreating(true);
        try {
            const payload = { ...form, code: form.code.toUpperCase(), discount_percent: parseInt(form.discount_percent), max_uses: form.max_uses ? parseInt(form.max_uses) : null, expires_at: form.expires_at ? form.expires_at + "T23:59:59" : null };
            await api.post("/admin/discount-codes", payload);
            setForm({ code: "", discount_percent: 10, plans: [], expires_at: "", max_uses: "", description: "" });
            await load();
        } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
        finally { setCreating(false); }
    };
    const toggleActive = async (item) => {
        try { await api.put(`/admin/discount-codes/${item.id}`, { active: !item.active }); await load(); } catch (e) { console.error(e); }
    };
    const removeCode = async (item) => {
        if (!window.confirm(`Eliminare il codice ${item.code}?`)) return;
        try { await api.delete(`/admin/discount-codes/${item.id}`); await load(); } catch (e) { console.error(e); }
    };

    return (
        <div data-testid="admin-discounts-tab" className="space-y-8">
            <div className="border border-white/10 p-6 bg-ink-900/40">
                <div className="flex items-center gap-3 mb-4"><Tag size={20} className="text-gold" /><h2 className="font-serif text-2xl text-ink-50">Nuovo codice sconto</h2></div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Codice</label>
                        <input data-testid="dc-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LANCIO50" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm font-mono uppercase" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">% sconto</label>
                        <input data-testid="dc-percent" type="number" min={1} max={100} value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Scadenza</label>
                        <input data-testid="dc-expires" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Max utilizzi</label>
                        <input data-testid="dc-max-uses" type="number" min={0} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                    <div className="flex items-end">
                        <button data-testid="dc-create" onClick={create} disabled={creating} className="w-full cta-gold py-2.5 text-sm flex items-center justify-center gap-2"><Plus size={14} /> Crea</button>
                    </div>
                    <div className="md:col-span-6">
                        <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Descrizione (interna)</label>
                        <input data-testid="dc-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Campagna marzo agenzie" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                    </div>
                </div>
                <p className="text-xs text-ink-400 mt-4">Il calcolo dello sconto è automatico al checkout: percentuale × prezzo del piano (o prezzo intro, se più basso).</p>
            </div>

            <div className="border border-white/10">
                {items.length === 0 ? <p className="p-6 text-sm text-ink-400">Nessun codice creato.</p> : (
                    <div className="space-y-2.5">
                        {items.map((c) => (
                            <div key={c.id} data-testid={`dc-row-${c.code}`} className="p-4 grid grid-cols-12 gap-3 items-center text-sm vd-card vd-lead-row">
                                <div className="col-span-2 font-mono font-bold text-gold">{c.code}</div>
                                <div className="col-span-1 text-ink-100 flex items-center gap-1"><Percent size={12} className="text-gold" /> {c.discount_percent}</div>
                                <div className="col-span-2 text-xs text-ink-400">{c.uses_count || 0}{c.max_uses ? `/${c.max_uses}` : ""} usi</div>
                                <div className="col-span-3 text-xs text-ink-400">{c.expires_at ? `Scade ${new Date(c.expires_at).toLocaleDateString("it-IT")}` : "Nessuna scadenza"}</div>
                                <div className="col-span-2"><span className={`px-2 py-1 text-xs ${c.active ? "bg-green-900/30 text-green-300" : "bg-ink-900 text-ink-400"}`}>{c.active ? "Attivo" : "Disattivo"}</span></div>
                                <div className="col-span-2 flex justify-end gap-2">
                                    <button data-testid={`dc-toggle-${c.code}`} onClick={() => toggleActive(c)} className="text-xs px-3 py-1 border border-white/15 hover:border-gold hover:text-gold">{c.active ? "Disattiva" : "Attiva"}</button>
                                    <button data-testid={`dc-delete-${c.code}`} onClick={() => removeCode(c)} className="p-1.5 hover:text-red-400"><Trash2 size={14} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const PRICING_PROFILE_META = {
    agenzia_pro: { label: "Agenzia Pro", icon: Building2, color: "text-blue-600", hint: "Piano premium — sincro feed illimitata, 5 annunci manuali" },
    agenzia_full: { label: "Agenzia Full", icon: Crown, color: "text-emerald-600", hint: "Piano top di gamma — VD Manager incluso, annunci illimitati" },
    privato: { label: "Privato", icon: Users, color: "text-amber-600", hint: "Piano per proprietari privati — 1 annuncio incluso. Prezzo mensile = paywall mostrato ai privati Free quando tentano di pubblicare un annuncio (banner \"Attiva Ora\" in /pubblica)." },
};

const NumField = ({ testid, label, value, onChange, disabled, prefix = "€", step = "0.01" }) => (
    <div>
        <label className="block text-[11px] text-ink-400 mb-1 uppercase tracking-wider">{label}</label>
        <div className="relative">
            {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-ink-400">{prefix}</span>}
            <input
                data-testid={testid}
                type="number"
                step={step}
                min="0"
                disabled={disabled}
                value={value ?? ""}
                onChange={(ev) => onChange(ev.target.value)}
                className={`w-full bg-transparent border border-white/15 py-2 text-ink-50 focus:border-gold outline-none text-sm ${prefix ? "pl-7 pr-3" : "px-3"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            />
        </div>
    </div>
);

const PricingProfilesTab = () => {
    const [profiles, setProfiles] = useState([]);
    const [active, setActive] = useState("agenzia_pro");
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const r = await api.get("/admin/pricing-profiles");
            setProfiles(r.data.profiles || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const profile = profiles.find((p) => p.profile_id === active);
    const e = edits[active] || {};
    const val = (field) => (e[field] !== undefined ? e[field] : profile?.[field]);
    const setField = (field, v) => setEdits({ ...edits, [active]: { ...e, [field]: v } });

    const save = async () => {
        const body = {};
        for (const [k, v] of Object.entries(e)) {
            if (v !== undefined && v !== "") body[k] = v;
        }
        if (!Object.keys(body).length) return;
        setSaving(true);
        try {
            const r = await api.put(`/admin/pricing-profiles/${active}`, body);
            const sync = r.data?.stripe_sync;
            if (sync && sync.ok === false) {
                toast.warning(`Profilo salvato, ma la sincronizzazione Stripe non è riuscita: ${sync.error || sync.radar_aste_error || sync.slots_error || "riprova"}`);
            } else {
                toast.success("Profilo salvato — Price ID sincronizzati su Stripe e prezzi attivi sul sito");
            }
            invalidatePromoCache();
            setEdits({ ...edits, [active]: {} });
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore nel salvataggio"); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="text-ink-400 text-sm" data-testid="pricing-profiles-loading">Caricamento...</div>;
    if (!profile) return null;

    const asteDisabled = !profile.radar_aste_available;
    const slotsDisabled = !profile.slots_available;

    return (
        <div data-testid="admin-pricing-profiles-tab" className="space-y-6">
            <div className="vd-card p-6">
                <h2 className="font-serif text-2xl text-ink-50 mb-2 flex items-center gap-2"><Coins size={20} className="text-amber-500" /> Prezzi Piani — Logica commerciale dinamica</h2>
                <p className="text-xs text-ink-400">Le modifiche sono salvate in <b className="text-ink-100">settings_pricing</b> e lette dal sito in tempo reale. Ad ogni salvataggio i Price ID vengono creati/aggiornati sul tuo account Stripe (piano, Radar Aste, pacchetti slot). Le sottoscrizioni <b className="text-ink-100">già attive</b> restano al prezzo pagato. Social Inbox e MLS Collaborazione sono salvati come configurazione in attesa dei rispettivi moduli.</p>
            </div>

            <div className="flex flex-wrap gap-3" data-testid="pricing-profile-switcher">
                {profiles.map((p) => {
                    const meta = PRICING_PROFILE_META[p.profile_id];
                    const Icon = meta.icon;
                    const isActive = active === p.profile_id;
                    return (
                        <button
                            key={p.profile_id}
                            data-testid={`pricing-profile-tab-${p.profile_id}`}
                            onClick={() => setActive(p.profile_id)}
                            className={`neu-btn no-pill inline-flex items-center gap-2.5 px-5 py-3 text-sm font-semibold whitespace-nowrap ${isActive ? "neu-btn-active" : ""}`}
                        >
                            <span className="neu-icon-chip"><Icon size={15} className={isActive ? "text-white" : meta.color} /></span>
                            {meta.label}
                            <span className="text-[11px] opacity-70">€{Number(p.monthly_price ?? 0).toFixed(2)}/mese</span>
                        </button>
                    );
                })}
            </div>

            <div className="vd-card p-6 space-y-8" data-testid={`pricing-profile-form-${active}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-white/10">
                    <div>
                        <input
                            data-testid={`pricing-profile-name-${active}`}
                            value={val("name") ?? ""}
                            onChange={(ev) => setField("name", ev.target.value)}
                            className="bg-transparent border-0 font-serif text-xl text-ink-50 focus:outline-none px-0"
                        />
                        <p className="text-[11px] text-ink-400 mt-0.5">{PRICING_PROFILE_META[active].hint}</p>
                    </div>
                    <button
                        data-testid={`pricing-profile-save-${active}`}
                        onClick={save}
                        disabled={saving}
                        className="cta-outline px-5 py-2.5 text-sm flex items-center gap-2"
                    >
                        <Save size={14} /> {saving ? "Salvataggio..." : "Salva profilo"}
                    </button>
                </div>

                {/* Prezzo base */}
                <div>
                    <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-3 flex items-center gap-1.5"><Coins size={12} /> Prezzo base</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <NumField testid={`pp-monthly-${active}`} label="Prezzo Mensile" value={val("monthly_price")} onChange={(v) => setField("monthly_price", v)} />
                        <NumField testid={`pp-annual-${active}`} label="Prezzo Annuale" value={val("annual_price")} onChange={(v) => setField("annual_price", v)} />
                        <NumField testid={`pp-slots-${active}`} label="Slot immobili inclusi" prefix="" step="1" value={val("included_slots")} onChange={(v) => setField("included_slots", v)} />
                        <div>
                            <NumField testid={`pp-free-months-${active}`} label="Mesi Gratis" prefix="" step="1" value={val("free_months")} onChange={(v) => setField("free_months", v)} />
                            <p className="text-[10px] text-ink-500 mt-1 flex items-center gap-1"><Gift size={10} className="text-gold" /> {Number(val("free_months") || 0) > 0 ? `${val("free_months")} mesi senza carta · 7 giorni di tolleranza` : "Nessun periodo gratuito"}</p>
                        </div>
                    </div>
                    {(profile.stripe_month_price_id || profile.stripe_year_price_id) && (
                        <p className="text-[10px] text-ink-500 mt-2 font-mono" data-testid={`pp-stripe-ids-${active}`}>
                            Stripe sync: {profile.stripe_month_price_id || "—"} (mensile) · {profile.stripe_year_price_id || "—"} (annuale)
                        </p>
                    )}
                </div>

                {/* Moduli extra */}
                <div>
                    <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-1 flex items-center gap-1.5"><Gauge size={12} /> Moduli extra</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="sm:col-span-2 space-y-1">
                            {asteDisabled && <p className="text-[11px] text-ink-500">Radar Aste non disponibile per il profilo {PRICING_PROFILE_META[active].label}.</p>}
                            <div className="grid grid-cols-2 gap-4">
                                <NumField testid={`pp-aste-month-${active}`} label="Radar Aste · mensile" value={val("radar_aste_monthly_price")} onChange={(v) => setField("radar_aste_monthly_price", v)} disabled={asteDisabled} />
                                <NumField testid={`pp-aste-year-${active}`} label="Radar Aste · annuale" value={val("radar_aste_annual_price")} onChange={(v) => setField("radar_aste_annual_price", v)} disabled={asteDisabled} />
                            </div>
                        </div>
                        <NumField testid={`pp-social-inbox-${active}`} label="Social Inbox" value={val("social_inbox_price")} onChange={(v) => setField("social_inbox_price", v)} />
                        <NumField testid={`pp-mls-${active}`} label="MLS Collaborazione" value={val("mls_collab_price")} onChange={(v) => setField("mls_collab_price", v)} />
                    </div>
                    <p className="text-[10px] text-ink-500 mt-2">Radar Aste è collegato a Stripe: al salvataggio i Price ID vengono aggiornati e il nuovo prezzo è subito visibile nel box di sblocco. Social Inbox e MLS Collaborazione sono in configurazione (moduli non ancora attivi).</p>
                </div>

                {/* Pacchetti slot */}
                <div>
                    <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-1 flex items-center gap-1.5"><Coins size={12} /> Pacchetti slot extra</h3>
                    {slotsDisabled && <p className="text-[11px] text-ink-500 mb-3">Non disponibile per il profilo {PRICING_PROFILE_META[active].label} (attivo solo su Agenzia Pro).</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <NumField testid={`pp-slot10-${active}`} label="Pacchetto +10 annunci" value={val("slot_price_10")} onChange={(v) => setField("slot_price_10", v)} disabled={slotsDisabled} />
                        <NumField testid={`pp-slot25-${active}`} label="Pacchetto +25 annunci" value={val("slot_price_25")} onChange={(v) => setField("slot_price_25", v)} disabled={slotsDisabled} />
                        <NumField testid={`pp-slot50-${active}`} label="Pacchetto +50 annunci" value={val("slot_price_50")} onChange={(v) => setField("slot_price_50", v)} disabled={slotsDisabled} />
                    </div>
                </div>

                {/* Target */}
                <div className="flex items-center justify-between gap-4 pt-4 border-t border-white/10">
                    <div>
                        <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-1">Applica a</h3>
                        <p className="text-sm text-ink-100">{val("target") === "new_only" ? "Solo nuovi iscritti" : "Tutti gli utenti"}</p>
                        <p className="text-[10px] text-ink-500 mt-0.5">
                            {val("target") === "new_only"
                                ? "I nuovi prezzi valgono solo per chi si registra dopo il salvataggio: gli utenti già registrati continuano a vedere e pagare i prezzi precedenti."
                                : "I nuovi prezzi valgono per tutti gli utenti (nuovi e già registrati). Le sottoscrizioni già attive restano comunque al prezzo pagato."}
                        </p>
                        {profile.legacy_effective_from && (
                            <p className="text-[10px] text-amber-500 mt-1" data-testid={`pp-legacy-info-${active}`}>
                                Prezzi precedenti attivi per gli iscritti prima del {new Date(profile.legacy_effective_from).toLocaleString("it-IT")}: €{Number(profile.legacy_monthly_price ?? 0).toFixed(2)}/mese · €{Number(profile.legacy_annual_price ?? 0).toFixed(2)}/anno
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-xs ${val("target") !== "new_only" ? "text-ink-500" : "text-ink-100"}`}>Solo nuovi iscritti</span>
                        <Switch
                            data-testid={`pp-target-switch-${active}`}
                            checked={val("target") !== "new_only"}
                            onCheckedChange={(checked) => setField("target", checked ? "all" : "new_only")}
                        />
                        <span className={`text-xs ${val("target") === "new_only" ? "text-ink-500" : "text-ink-100"}`}>Tutti gli utenti</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AuditLogTab = () => {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/admin/audit-log").then((r) => setItems(r.data.items || [])).catch(() => { /* noop */ });
    }, []);
    return (
        <div data-testid="admin-audit-tab" className="space-y-4">
            <div className="flex items-center gap-3 mb-2"><History size={18} className="text-gold" /><h2 className="font-serif text-2xl text-ink-50">Audit log azioni admin</h2></div>
            <p className="text-xs text-ink-400 mb-4">Tutte le azioni dell'amministratore vengono registrate per tracciabilità. Le sospensioni utente, modifiche prezzi, codici sconto e note sono qui.</p>
            {items.length === 0 ? <p className="text-sm text-ink-400">Nessun log ancora.</p> : (
                <div className="space-y-2.5">
                    {items.map((it) => (
                        <div key={it.id} className="p-3 grid grid-cols-12 gap-3 items-center text-sm vd-card vd-lead-row">
                            <div className="col-span-3 text-xs text-ink-400">{new Date(it.created_at).toLocaleString("it-IT")}</div>
                            <div className="col-span-3 text-ink-50 font-mono text-xs">{it.action}</div>
                            <div className="col-span-2 text-xs text-ink-400 truncate">{it.admin_email}</div>
                            <div className="col-span-4 text-xs text-ink-100 font-mono truncate">{JSON.stringify(it.payload)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


function AdminOfficesOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/admin/offices/overview").then((r) => setData(r.data)).catch(() => setData({ items: [], agencies_with_offices: 0, global_monthly_total: 0 })).finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="text-ink-400 text-sm">Caricamento...</div>;
    return (
        <div data-testid="admin-offices-overview">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="border border-white/10 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400">Agenzie con uffici</div>
                    <div className="font-serif text-2xl text-ink-50 mt-1">{data.agencies_with_offices}</div>
                </div>
                <div className="border border-gold/40 bg-gold/5 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-gold">MRR totale multi-sede</div>
                    <div className="font-serif text-2xl text-price mt-1">€ {Number(data.global_monthly_total || 0).toFixed(2)}</div>
                </div>
                <div className="border border-white/10 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400">Uffici totali</div>
                    <div className="font-serif text-2xl text-ink-50 mt-1">{(data.items || []).reduce((a, x) => a + (x.office_count || 0), 0)}</div>
                </div>
            </div>
            {(data.items || []).length === 0 ? (
                <div className="border border-dashed border-white/10 p-8 text-center text-sm text-ink-400">Nessuna agenzia ha ancora attivato uffici aggiuntivi.</div>
            ) : (
                <div className="space-y-4">
                    {(data.items || []).map((a) => (
                        <div key={a.agency_id} className="border border-white/10 p-5">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-white/10">
                                <div>
                                    <div className="font-serif text-lg text-ink-50">{a.agency_name}</div>
                                    <div className="text-xs text-ink-400">{a.agency_email}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-serif text-xl text-price">€ {Number(a.monthly_total).toFixed(2)}<span className="text-[11px] text-ink-400 ml-1">/mese totale</span></div>
                                    <div className="text-[10px] text-ink-400 mt-0.5">{a.active_count} attivi · {a.office_count} totali</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(a.offices || []).map((o) => (
                                    <div key={o.id} className="border border-white/10 p-3 flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-serif text-sm text-ink-50 truncate">{o.name}</span>
                                                <span className="text-[9px] text-ink-500">sede n°{o.order}</span>
                                            </div>
                                            <div className="text-[11px] text-ink-400 mt-0.5">{o.city}</div>
                                            <code className="inline-block mt-2 px-1.5 py-0.5 border border-gold/40 bg-gold/5 text-gold font-mono text-[10px]">{o.code}</code>
                                            {o.redeemed_by_email && <div className="text-[10px] text-ink-500 mt-1 truncate">→ {o.redeemed_by_email}</div>}
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-sm text-price">€ {Number(o.price_monthly).toFixed(0)}</div>
                                            <div className={`text-[9px] uppercase tracking-wider mt-1 ${o.status === "active" ? "text-green-400" : o.status === "cancelled" ? "text-red-300" : "text-amber-300"}`}>{o.status}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

